/**
 * Integrytas Group - Common Security Module (CSM)
 * Public approval page reached via the magic link in approval emails:
 *   /approve?token=<uuid>                  -> full review form
 *   /approve?token=<uuid>&action=approve_all -> auto-approve all roles, prompt for note
 *   /approve?token=<uuid>&action=reject_all  -> auto-reject all roles, prompt for note
 *
 * No authentication is required - the token IS the auth.
 *   - csm_load_approval_token (anon RPC) returns the request snapshot.
 *   - csm_signoff_via_token   (anon RPC) records the decision and burns
 *     the token in a single transaction.
 */

/* rbac-affordance-ok: there is no page RBAC to consult here — this route is
   deliberately unauthenticated (/approve?token=…, not a csm_page_master page)
   and the TOKEN is the authority. csm_signoff_via_token burns the token and is
   granted to anon by design. A session-based gate would break the email-link
   approval flow outright. */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { Watermark } from '../../components/common/Watermark';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../../constants/branding';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';

// §17.B11: labelled constant for per-role decision codes (CHECK enum).
const DECISION_META: Record<'APPROVE' | 'REJECT', { labelKey: string; label: string; variant: StatusVariant }> = {
  APPROVE: { labelKey: 'approval.page.decision.approve', label: 'Approve', variant: 'success' },
  REJECT:  { labelKey: 'approval.page.decision.reject',  label: 'Reject',  variant: 'danger'  },
};

type LoadStatus = 'OK' | 'NOT_FOUND' | 'USED' | 'EXPIRED' | 'SUPERSEDED' | 'ERROR' | 'LOADING';

interface RoleLine {
  user_provision_request_role_id: number;
  role_internal_code: string;
  role_display_text: string;
  status_internal_code: 'PENDING' | 'APPROVED' | 'REJECTED';
  decision: 'APPROVE' | 'REJECT';
}

interface HistoryEntry {
  sequence_order: number;
  signer_name: string;
  signed_at: string;
  comment: string | null;
  per_role: { role_display_text: string; decision: 'APPROVE' | 'REJECT' }[];
}

interface RequestSnapshot {
  request_internal_code: string;
  target_email: string;
  target_display_name: string;
  business_reason: string | null;
  submitted_at: string;
  sequence_order: number;
}

interface LoadedData {
  request: RequestSnapshot;
  job: { internal_code: string; display_text: string };
  requester: { display_name: string; email: string };
  approver: { display_name: string; email: string };
  roles: RoleLine[];
  history: HistoryEntry[];
}

export interface ApprovalPageProps {
  token: string;
  action?: string | null;   // 'approve_all' | 'reject_all' | null
}

export function ApprovalPage({ token, action }: ApprovalPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<LoadStatus>('LOADING');
  const [data, setData]     = useState<LoadedData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [comment, setComment]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]   = useState<{ status: string; finalized: boolean } | null>(null);

  // Load
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: payload, error } = await apiDb.rpc('csm_load_approval_token', { p_token: token });
      if (cancelled) return;
      if (error) {
        setStatus('ERROR'); setErrorMsg(error.message); return;
      }
      type TokenRoleRow = {
        user_provision_request_role_id: number;
        role_internal_code: string;
        role_display_text: string;
        status_internal_code: 'PENDING' | 'APPROVED' | 'REJECTED';
      };
      type TokenPayload = {
        status?: string;
        request: RequestSnapshot;
        job: { internal_code: string; display_text: string };
        requester: { display_name: string; email: string };
        approver: { display_name: string; email: string };
        roles?: TokenRoleRow[];
        history?: HistoryEntry[];
      };
      const obj = payload as TokenPayload | null;
      if (!obj || typeof obj !== 'object' || !obj.status) {
        setStatus('ERROR'); setErrorMsg('Unexpected response shape.'); return;
      }
      if (obj.status !== 'OK') {
        setStatus(obj.status as LoadStatus);
        return;
      }
      const roles: RoleLine[] = (obj.roles ?? []).map((r: TokenRoleRow) => ({
        user_provision_request_role_id: r.user_provision_request_role_id,
        role_internal_code: r.role_internal_code,
        role_display_text: r.role_display_text,
        status_internal_code: r.status_internal_code,
        decision:
          action === 'reject_all'  ? 'REJECT'  :
          action === 'approve_all' ? 'APPROVE' :
                                     (r.status_internal_code === 'PENDING' ? 'APPROVE' : 'APPROVE'),
      }));
      setData({
        request: obj.request,
        job: obj.job,
        requester: obj.requester,
        approver: obj.approver,
        roles,
        history: obj.history ?? [],
      });
      setStatus('OK');
    })();
    return () => { cancelled = true; };
  }, [token, action]);

  const setRoleDecision = (id: number, dec: 'APPROVE' | 'REJECT') => {
    setData(prev => prev && ({
      ...prev,
      roles: prev.roles.map(r =>
        r.user_provision_request_role_id === id ? { ...r, decision: dec } : r),
    }));
  };

  const submit = async () => {
    if (!data) return;
    setSubmitting(true); setErrorMsg(null);
    try {
      const decisions = data.roles
        .filter(r => r.status_internal_code === 'PENDING')
        .map(r => ({
          user_provision_request_role_id: r.user_provision_request_role_id,
          decision: r.decision,
        }));
      if (decisions.length === 0) {
        throw new Error('No pending roles remain.');
      }
      const { data: result, error } = await apiDb.rpc('csm_signoff_via_token', {
        p_token: token,
        p_role_decisions: decisions,
        p_comment: comment || null,
      });
      if (error) throw error;
      const row = Array.isArray(result) ? result[0] : result;
      if (!row || row.message !== 'OK') {
        throw new Error(row?.message ?? 'Token rejected.');
      }
      setDone({ status: row.next_status_code, finalized: row.finalized });
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const allApprove = useMemo(
    () => data?.roles.every(r => r.status_internal_code !== 'PENDING' || r.decision === 'APPROVE') ?? false,
    [data],
  );
  const allReject = useMemo(
    () => data?.roles.every(r => r.status_internal_code !== 'PENDING' || r.decision === 'REJECT') ?? false,
    [data],
  );

  return (
    <Frame>
      {status === 'LOADING' && <SkeletonRows rows={8} />}

      {status === 'NOT_FOUND' && (
        <Banner tone="error" title={t('approval.banner.not_found_title')}>
          {t('approval.banner.not_found_body')}
        </Banner>
      )}

      {status === 'USED' && (
        <Banner tone="warn" title={t('approval.banner.used_title')}>
          {t('approval.banner.used_body')}
        </Banner>
      )}

      {status === 'EXPIRED' && (
        <Banner tone="warn" title={t('approval.banner.expired_title')}>
          {t('approval.banner.expired_body')}
        </Banner>
      )}

      {status === 'SUPERSEDED' && (
        <Banner tone="warn" title={t('approval.banner.superseded_title')}>
          {t('approval.banner.superseded_body')}
        </Banner>
      )}

      {status === 'ERROR' && (
        <ErrorBanner
          message={t('approval.banner.error_title')}
          detail={errorMsg ?? t('approval.banner.error_unknown')}
          details_label={t('common.details', 'Details')}
        />
      )}

      {status === 'OK' && data && !done && (
        <>
          <h1 className="mb-2 text-csm-lg font-semibold text-csm-ink">
            {t('approval.page.title')}
          </h1>
          <p className="mb-4 text-csm-sm text-csm-ink-soft">
            {t('approval.page.greeting', {
              approver: data.approver.display_name,
              requester: data.requester.display_name,
              step: data.request.sequence_order,
            })}
          </p>

          <Card>
            <Row k={t('approval.page.card.request')}      v={<code className="text-csm-sm">{data.request.request_internal_code}</code>} />
            <Row k={t('approval.page.card.target_name')}  v={data.request.target_display_name} />
            <Row k={t('approval.page.card.target_email')} v={data.request.target_email} />
            <Row k={t('approval.page.card.job')}          v={`${data.job.display_text} (${data.job.internal_code})`} />
            <Row k={t('approval.page.card.reason')}       v={data.request.business_reason || <em className="text-csm-watermark">{t('approval.page.card.reason_none')}</em>} />
            <Row k={t('approval.page.card.requested_by')} v={`${data.requester.display_name} <${data.requester.email}>`} />
            <Row k={t('approval.page.card.submitted')}    v={new Date(data.request.submitted_at).toLocaleString()} />
          </Card>

          <h2 className="mb-1 mt-4 text-csm-md font-semibold text-csm-ink">{t('approval.page.roles_heading')}</h2>
          <p className="mb-2 text-csm-xs text-csm-muted">
            {t('approval.page.roles_subtitle')}
          </p>
          <table className="mb-2 w-full border border-csm-border text-csm-md">
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr>
                <th className="border-b border-csm-border px-2 py-1 text-left">{t('approval.page.roles_columns.role')}</th>
                <th className="border-b border-csm-border px-2 py-1 text-left">{t('approval.page.roles_columns.status')}</th>
                <th className="border-b border-csm-border px-2 py-1 text-left">{t('approval.page.roles_columns.your_decision')}</th>
              </tr>
            </thead>
            <tbody>
              {data.roles.map(r => (
                <tr key={r.user_provision_request_role_id} className="border-t border-csm-border-soft">
                  <td className="px-2 py-1.5">
                    <div>{r.role_display_text}</div>
                    <div className="font-mono text-csm-2xs text-csm-muted">{r.role_internal_code}</div>
                  </td>
                  <td className="px-2 py-1.5"><RoleStatus code={r.status_internal_code} /></td>
                  <td className="px-2 py-1.5">
                    {r.status_internal_code === 'PENDING' ? (
                      <div className="inline-flex overflow-hidden rounded border border-csm-border">
                        <button
                          onClick={() => setRoleDecision(r.user_provision_request_role_id, 'APPROVE')}
                          className={
                            'px-2 py-0.5 text-csm-xs ' +
                            (r.decision === 'APPROVE' ? 'bg-csm-success text-white' : 'bg-white text-csm-ink-soft hover:bg-csm-surface-alt')
                          }
                        >{t('approval.page.decision.approve')}</button>
                        <button
                          onClick={() => setRoleDecision(r.user_provision_request_role_id, 'REJECT')}
                          className={
                            'border-l border-csm-border px-2 py-0.5 text-csm-xs ' +
                            (r.decision === 'REJECT' ? 'bg-csm-danger text-white' : 'bg-white text-csm-ink-soft hover:bg-csm-surface-alt')
                          }
                        >{t('approval.page.decision.reject')}</button>
                      </div>
                    ) : (
                      <span className="text-csm-xs italic text-csm-watermark">{t('approval.page.decision.already_finalized')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mb-4 flex gap-2 text-csm-xs">
            <button
              type="button"
              onClick={() => setData(prev => prev && ({
                ...prev, roles: prev.roles.map(r => r.status_internal_code === 'PENDING' ? { ...r, decision: 'APPROVE' } : r),
              }))}
              className="rounded border border-csm-success-border bg-csm-success-soft px-2 py-0.5 text-csm-success hover:bg-csm-success-soft"
            >{t('approval.page.bulk.approve_all')}</button>
            <button
              type="button"
              onClick={() => setData(prev => prev && ({
                ...prev, roles: prev.roles.map(r => r.status_internal_code === 'PENDING' ? { ...r, decision: 'REJECT' } : r),
              }))}
              className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-danger hover:bg-csm-danger-soft"
            >{t('approval.page.bulk.reject_all')}</button>
            {(allApprove || allReject) && (
              <span className="text-csm-muted italic">
                {allApprove ? t('approval.page.bulk.currently_approve_all') : t('approval.page.bulk.currently_reject_all')}
              </span>
            )}
          </div>

          <h2 className="mb-1 mt-4 text-csm-md font-semibold text-csm-ink">
            {t('approval.page.history_heading')}
          </h2>
          <div className="mb-4 space-y-2">
            {data.history.length === 0 && (
              <EmptyState variant="no-data" title={t('approval.page.history_empty')} />
            )}
            {data.history.map(h => (
              <div key={h.sequence_order} className="rounded border border-csm-border bg-white px-3 py-2 text-csm-sm">
                <div className="mb-1">
                  <span className={
                    'rounded border px-1.5 py-0.5 text-csm-2xs ' +
                    (h.sequence_order === 0
                      ? 'border-csm-info-border bg-csm-info-soft text-csm-info uppercase tracking-wider'
                      : 'border-csm-border bg-csm-surface-alt')
                  }>
                    {h.sequence_order === 0 ? t('approval.page.history_step_manager') : t('approval.page.history_step_n', { n: h.sequence_order })}
                  </span>
                  <strong className="ml-2">{h.signer_name}</strong>
                  <span className="ml-2 text-csm-muted">{new Date(h.signed_at).toLocaleString()}</span>
                </div>
                <ul className="mb-1 ml-4 list-disc">
                  {h.per_role.map((p, i) => (
                    <li key={i}>
                      <StatusBadge
                        label={t(DECISION_META[p.decision].labelKey, DECISION_META[p.decision].label)}
                        variant={DECISION_META[p.decision].variant}
                      />{' '}{p.role_display_text}
                    </li>
                  ))}
                </ul>
                {h.comment && (
                  <div className="text-csm-ink-soft italic">"{h.comment}"</div>
                )}
              </div>
            ))}
          </div>

          <h2 className="mb-1 text-csm-md font-semibold text-csm-ink">{t('approval.page.your_note_heading')}</h2>
          <textarea
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={t('approval.page.your_note_placeholder')}
            className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-md focus:border-csm-watermark focus:outline-none"
          />

          {errorMsg && (
            <ErrorBanner
              message={t('approval.banner.submit_error_title')}
              detail={errorMsg}
              details_label={t('common.details', 'Details')}
            />
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => void submit()}
              disabled={submitting}
              className="rounded border border-primary bg-primary px-4 py-1.5 text-csm-md text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {submitting ? t('approval.page.submitting') : t('approval.page.submit')}
            </button>
          </div>
        </>
      )}

      {done && (
        <Banner tone={done.status === 'APPROVED' ? 'ok' : done.status === 'REJECTED' ? 'warn' : 'ok'}
                title={
                  done.status === 'APPROVED' ? t('approval.done.approved_title') :
                  done.status === 'REJECTED' ? t('approval.done.rejected_title') :
                                                t('approval.done.advanced_title')
                }>
          {done.finalized
            ? t('approval.done.thanks_finalized')
            : t('approval.done.thanks_advanced')}
        </Banner>
      )}
    </Frame>
  );
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen bg-csm-surface-alt py-10">
      <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />
      <div className="mx-auto max-w-2xl rounded border border-csm-border bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  );
}

function Banner({ tone, title, children }: {
  tone: 'ok' | 'warn' | 'error';
  title: string;
  children?: React.ReactNode;
}) {
  const cls =
    tone === 'ok'    ? 'border-csm-success-border bg-csm-success-soft text-csm-success' :
    tone === 'warn'  ? 'border-csm-warning-border bg-csm-warning-soft text-csm-warning' :
                       'border-csm-danger-border bg-csm-danger-soft text-csm-danger';
  return (
    <div className={'rounded border px-3 py-2 text-csm-md ' + cls}>
      <div className="mb-1 font-semibold">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <table className="w-full border-collapse text-csm-md">
      <tbody>{children}</tbody>
    </table>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr>
      <td className="py-1 pr-3 align-top text-csm-muted" style={{ width: '7.5rem' }}>{k}</td>
      <td className="py-1 align-top">{v}</td>
    </tr>
  );
}

function RoleStatus({ code }: { code: 'PENDING' | 'APPROVED' | 'REJECTED' }) {
  const { t } = useTranslation();
  const variant: StatusVariant =
    code === 'APPROVED' ? 'success' :
    code === 'REJECTED' ? 'danger' :
                          'warning';
  const label =
    code === 'APPROVED' ? t('approval.role_status.approved') :
    code === 'REJECTED' ? t('approval.role_status.rejected') :
                          t('approval.role_status.pending');
  return <StatusBadge label={label} variant={variant} />;
}
